import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Divider } from '@lightspeed/flame/Divider';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import Text from '@lightspeed/cirrus-text';
import { Alert } from '@lightspeed/flame/Alert';
import get from 'lodash/get';
import zipObject from 'lodash/zipObject';

import { entities } from 'pages/store/schema';
import { getData, putData, clearData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import {
  getProcessing,
  getErrors,
  getMappablePaymentTypes,
  getLightspeedPaymentGateways,
  getLightspeedPaymentParameters,
  getLightspeedPaymentCardMappings,
} from 'pages/store/selectors';

import Layout, { LayoutContent, LayoutSection } from 'cirrus/Layout';

import PaymentProvider from 'app/src/payments/PaymentProvider';
import Header from './Header';
import GatewayConfigListing from './GatewayConfig/Listing';
import GatewayParameter from '../GatewayParameter';
import PaymentTypeMapping from '../PaymentTypeMapping';

const merchantos = window.merchantos;
const iconSize = '1.125rem';

const STRINGS = {
  SHOPS: {
    TITLE: 'Shops',
    DESCRIPTION: 'Add payment terminal to process payments in selected locations',
  },
  SECURITY_OPTIONS: {
    TITLE: 'Security Options',
    LABEL: 'Allow Credits',
    DESCRIPTION: 'Allow refunds without original charge',
  },
  PARTIAL_APPROVALS: {
    TITLE: 'Partial Approvals',
    LABEL: 'Allow Partial Approvals',
    DESCRIPTION:
      'If activated, the terminal can accept an authorization response for an amount that is less than the amount requested.',
  },
  DUPLICATES: {
    TITLE: 'Duplicate Sales',
    LABEL: 'Prevent Duplicate Sales',
    DESCRIPTION:
      'If activated, two or more transactions for the same amount in a short period of time will not be allowed. It is intended to reduce the risk of charging a customer twice by mistake.',
  },
  MAGSTRIPE_FALLBACK: {
    TITLE: 'Magnetic Stripe Fallback',
    LABEL: 'Allow Magnetic Stripe Fallback',
    DESCRIPTION:
      "If activated and the card details can't be obtained from the card chip, the magnetic stripe reader is proposed as an alternative card entry method.",
  },
  PAYMENT_MAPPING: {
    TITLE: 'Credit Card Payment Mapping',
    DESCRIPTION: 'Select how card brands map to your payment types',
  },
  ERROR: {
    SECTION_UNAVAILABLE: {
      TITLE: 'Section Unavailable',
      MESSAGE:
        "This section is currently unavailable. We're aware of the situation, please retry later.",
    },
    SAVE_ERROR: {
      TITLE: 'Gateway Configuration not Saved',
      MESSAGE:
        'The gateway configuration could not be saved.  If this problem persists, please contact customer support.',
    },
  },
};

export const formatInitialValues = ({ gatewayParameters = {}, cardMappings = [] }) => {
  const cardSchemeMappings = zipObject(
    cardMappings.map(mapping => `cardMapping-${get(mapping, 'cardSchemeID')}`),
    cardMappings.map(mapping => get(mapping, 'paymentTypeID'))
  );

  return {
    allowCredits: get(gatewayParameters, 'allowCredits', 'false') === 'true',
    partialApprovals: get(gatewayParameters, 'partialApprovals', 'false') === 'true',
    checkForDuplicateTransactions:
      get(gatewayParameters, 'checkForDuplicateTransactions', 'false') === 'true',
    magstripeFallback: get(gatewayParameters, 'magstripeFallback', 'false') === 'true',
    ...cardSchemeMappings,
  };
};

export class ConfigurationComponent extends Component {
  constructor(props) {
    super(props);

    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
            icon: 'settings',
          },
          {
            text: 'Lightspeed Payments',
          },
        ],
      },
    });

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.props.getData(
      `/API/Account/${merchantos.account.id}/CCTerminal.json`,
      entities.ccTerminal
    );
    this.props.getData(
      `/API/Account/${merchantos.account.id}/GatewayConfig.json`,
      entities.gatewayConfig
    );
    this.props.getData(
      `/API/Account/${merchantos.account.id}/PaymentType.json`,
      entities.paymentType
    );
    this.props.getData(`/API/Account/${merchantos.account.id}/Shop.json`, entities.shop);
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  handleSubmit(values = {}) {
    const { gatewayConfigs, cardMappings } = this.props;
    const {
      allowCredits,
      partialApprovals,
      checkForDuplicateTransactions,
      magstripeFallback,
    } = values;

    const cardSchemeMappings = cardMappings.map(mapping => {
      const newPaymentTypeID = get(values, `cardMapping-${get(mapping, 'cardSchemeID')}`);

      return {
        ...mapping,
        paymentTypeID: newPaymentTypeID,
      };
    });

    gatewayConfigs.forEach(({ gatewayConfigID, gatewayID, name, parameters }) => {
      const payload = {
        gatewayConfigID,
        gatewayID,
        name,
        cardSchemeMappings: {
          CardSchemeMapping: cardSchemeMappings,
        },
        parameters: {
          ...parameters,
          allowCredits,
          partialApprovals,
          checkForDuplicateTransactions,
          magstripeFallback,
        },
      };

      if (gatewayConfigID) {
        this.props.saveData(gatewayConfigID, payload);
      }
    });
  }

  render() {
    const {
      gatewayConfigs,
      paymentTypes,
      cardMappings,
      isLoading,
      isErroring,
      isSaving,
      saveErrors,
    } = this.props;
    const initialValues = formatInitialValues(this.props);
    const {
      supportAllowCredits,
      supportPartialApprovals,
      supportPreventDuplicates,
      supportMagstripeFallback,
    } = PaymentProvider.supportedFeaturesForGateway(
      // We do not expect an account to have more than one LSP gateway config.
      gatewayConfigs[0]
    );

    return isErroring ? (
      <Layout>
        <LayoutContent>
          <Card large>
            <CardSection>
              <div className="cr-flex cr-flex-wrap">
                <div className="cr-flex cr-width-full">
                  <div className="cr-flex cr-width-full cr-flex-col">
                    <div className="cr-flex cr-width-full cr-items-center">
                      <div className="cr-mr-2 cr-width-full" style={{ width: iconSize }}>
                        <IconWarning size="iconSize" color="maple" />
                      </div>
                      <Text heading3>{STRINGS.ERROR.SECTION_UNAVAILABLE.TITLE}</Text>
                    </div>
                    <div className="cr-flex cr-width-full cr-items-center cr-mt-2">
                      <div className="cr-mr-2" style={{ minWidth: iconSize, height: iconSize }} />
                      <div className="cr-width-full">
                        <Text bodySmall dimmed>
                          {STRINGS.ERROR.SECTION_UNAVAILABLE.MESSAGE}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardSection>
          </Card>
        </LayoutContent>
      </Layout>
    ) : (
      <Formik
        enableReinitialize
        onSubmit={this.handleSubmit}
        initialValues={initialValues}
        render={formikProps => {
          const { dirty, handleSubmit } = formikProps;

          return (
            <Fragment>
              <form name="ls-configuration-form" onSubmit={handleSubmit}>
                <Header dirty={dirty} isSaving={isSaving} />

                {saveErrors && (
                  <div
                    className="cr-max-width-content cr-display-block-centered cr-mt-2"
                    data-test="settings-optional-modules-alert"
                  >
                    <Alert type="danger" title={STRINGS.ERROR.SAVE_ERROR.TITLE}>
                      {STRINGS.ERROR.SAVE_ERROR.MESSAGE}
                    </Alert>
                  </div>
                )}

                <Layout>
                  <LayoutSection
                    title={STRINGS.SHOPS.TITLE}
                    description={STRINGS.SHOPS.DESCRIPTION}
                  >
                    <GatewayConfigListing gatewayConfigs={gatewayConfigs} isLoading={isLoading} />
                  </LayoutSection>

                  {supportAllowCredits && (
                    <>
                      <Divider />
                      <LayoutSection
                        title={STRINGS.SECURITY_OPTIONS.TITLE}
                        description={STRINGS.SECURITY_OPTIONS.DESCRIPTION}
                      >
                        <GatewayParameter
                          isLoading={isLoading}
                          label={STRINGS.SECURITY_OPTIONS.LABEL}
                          fieldName="allowCredits"
                        />
                      </LayoutSection>
                    </>
                  )}

                  {supportPartialApprovals && (
                    <>
                      <Divider />
                      <LayoutSection
                        title={STRINGS.PARTIAL_APPROVALS.TITLE}
                        description={STRINGS.PARTIAL_APPROVALS.DESCRIPTION}
                      >
                        <GatewayParameter
                          isLoading={isLoading}
                          label={STRINGS.PARTIAL_APPROVALS.LABEL}
                          fieldName="partialApprovals"
                        />
                      </LayoutSection>
                    </>
                  )}

                  {supportPreventDuplicates && (
                    <>
                      <Divider />
                      <LayoutSection
                        title={STRINGS.DUPLICATES.TITLE}
                        description={STRINGS.DUPLICATES.DESCRIPTION}
                      >
                        <GatewayParameter
                          isLoading={isLoading}
                          label={STRINGS.DUPLICATES.LABEL}
                          fieldName="checkForDuplicateTransactions"
                        />
                      </LayoutSection>
                    </>
                  )}

                  {supportMagstripeFallback && (
                    <>
                      <Divider />
                      <LayoutSection
                        title={STRINGS.MAGSTRIPE_FALLBACK.TITLE}
                        description={STRINGS.MAGSTRIPE_FALLBACK.DESCRIPTION}
                      >
                        <GatewayParameter
                          isLoading={isLoading}
                          label={STRINGS.MAGSTRIPE_FALLBACK.LABEL}
                          fieldName="magstripeFallback"
                        />
                      </LayoutSection>
                    </>
                  )}

                  <Divider />
                  <LayoutSection
                    title={STRINGS.PAYMENT_MAPPING.TITLE}
                    description={STRINGS.PAYMENT_MAPPING.DESCRIPTION}
                  >
                    <PaymentTypeMapping
                      paymentTypes={paymentTypes}
                      cardMappings={cardMappings}
                      isLoading={isLoading}
                    />
                  </LayoutSection>
                </Layout>
              </form>
            </Fragment>
          );
        }}
      />
    );
  }
}

ConfigurationComponent.defaultProps = {
  isLoading: true,
  isErroring: false,
};

const mapStateToProps = state => ({
  gatewayConfigs: getLightspeedPaymentGateways(state),
  gatewayParameters: getLightspeedPaymentParameters(state),
  paymentTypes: getMappablePaymentTypes(state),
  cardMappings: getLightspeedPaymentCardMappings(state),
  gatewayConfigErrors: getErrors({
    entity: entities.gatewayConfig,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  isErroring:
    getErrors({ entity: entities.ccTerminal, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getErrors({ entity: entities.gatewayConfig, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getErrors({ entity: entities.paymentType, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getErrors({ entity: entities.shop, list: true, actionType: actionTypes.LOADING })(state),
  isLoading:
    getProcessing({ entity: entities.ccTerminal, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.gatewayConfig, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.paymentType, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.shop, list: true, actionType: actionTypes.LOADING })(state),
  isSaving: getProcessing({
    entity: entities.gatewayConfig,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  saveErrors: getErrors({
    entity: entities.gatewayConfig,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  getData: (url, entity) => dispatch(getData({ url, entity, list: true })),
  saveData: (gatewayConfigId, payload) =>
    dispatch(
      putData({
        url: `/API/Account/${merchantos.account.id}/GatewayConfig/${gatewayConfigId}.json`,
        payload,
        entity: entities.gatewayConfig,
      })
    ),
  clearData: () => {
    dispatch(
      clearData([entities.gatewayConfig, entities.shop, entities.ccTerminal, entities.paymentType])
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationComponent);
